/**
 * Declarative click tracking (loaded once, deferred, from layout/theme.liquid).
 *
 * Mirrors the Murphy Door theme's bridge (murphy-door-shopify assets/analytics-tracking.js)
 * so MD and Ultra Shelf reports line up: the same attribute contract and the same
 * payload fields.
 *
 *   <a href="..." data-track-click="click_announcement_cta"
 *      data-track-section="announcement_bar" data-track-label="<block id>"
 *      data-track-position="1">
 *
 * One delegated click listener resolves the nearest [data-track-click] ancestor and
 * sends one event to three feature-detected sinks:
 *   1. GA4 via the Google & YouTube app's gtag (G-TM2HDJFHKY), event `element_click`,
 *      transport_type 'beacon' so a navigating click still delivers.
 *   2. window.dataLayer.push({ event: 'element_click', ... }) for any tag manager.
 *   3. Shopify.analytics.publish('md_element_click', payload) for a custom web pixel.
 *
 * Consent: when Shopify's Customer Privacy API is loaded and
 * analyticsProcessingAllowed() is false (visitor declined, or a region that needs
 * opt-in), nothing is sent to any sink. When the API is not loaded, gtag's own consent
 * mode and the pixel sandbox still govern their sinks.
 *
 * Internal traffic: if window.MD_INTERNAL_TRAFFIC === true (the Murphy Door theme's
 * staff flag) nothing is sent, the same check as MD's bridge. Ultra Shelf has no
 * setter for it yet (MD's is an inline <head> script with a secret token digest), so
 * today this only honours the flag if one is ever added.
 *
 * Rules: never throws, never calls preventDefault, never blocks navigation (passive
 * listener). No PII: labels are block/section ids or slugs set in markup, never
 * customer data, form values or discount code text. No visibility sentinels.
 */
(function () {
  'use strict';

  if (window.usAnalyticsTracking) return;

  var GA4_ID = 'G-TM2HDJFHKY';
  var EVENT = 'element_click';
  var PIXEL_EVENT = 'md_element_click';

  /**
   * Page context from <body data-*> (layout/theme.liquid), the same fields as MD's
   * bodyCtx(): page_type, template, product_id, collection_id ('' when absent).
   * @returns {object}
   */
  function bodyCtx() {
    var bd = (document.body && document.body.dataset) || {};
    return {
      page_type: bd.pageType || '',
      template: bd.template || '',
      product_id: bd.productId || '',
      collection_id: bd.collectionId || ''
    };
  }

  function isInternalTraffic() {
    try {
      return window.MD_INTERNAL_TRAFFIC === true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Builds the payload for a tracked element.
   * @param {HTMLElement} el - Element carrying data-track-click.
   * @returns {object}
   */
  function payloadFor(el) {
    var d = el.dataset;
    var payload = {
      element_action: d.trackClick || '',
      element_section: d.trackSection || '',
      element_label: d.trackLabel || '',
      element_position: d.trackPosition || ''
    };
    var ctx = bodyCtx();
    Object.keys(ctx).forEach(function (key) {
      payload[key] = ctx[key];
    });
    return payload;
  }

  /**
   * Shopify Customer Privacy gate (merchant settings + region + visitor choice).
   * @returns {boolean} False only when the API is present and says no.
   */
  function analyticsProcessingAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
        return privacy.analyticsProcessingAllowed() !== false;
      }
    } catch (e) {
      /* fall through */
    }
    return true;
  }

  /**
   * Sends one tracked event to every available sink. Each sink is isolated so a
   * failure in one never stops the others or the click itself.
   * @param {object} payload - Event fields.
   */
  function send(payload) {
    // Consent gate: Shopify.customerPrivacy.analyticsProcessingAllowed() (see above).
    if (!analyticsProcessingAllowed()) return;
    if (isInternalTraffic()) return;

    try {
      if (typeof window.gtag === 'function') {
        var params = { send_to: GA4_ID, transport_type: 'beacon' };
        Object.keys(payload).forEach(function (key) {
          params[key] = payload[key];
        });
        window.gtag('event', EVENT, params);
      }
    } catch (e) {
      /* silent */
    }

    try {
      window.dataLayer = window.dataLayer || [];
      var message = { event: EVENT };
      Object.keys(payload).forEach(function (key) {
        message[key] = payload[key];
      });
      window.dataLayer.push(message);
    } catch (e) {
      /* silent */
    }

    try {
      if (
        window.Shopify
        && window.Shopify.analytics
        && typeof window.Shopify.analytics.publish === 'function'
      ) {
        window.Shopify.analytics.publish(PIXEL_EVENT, payload);
      }
    } catch (e) {
      /* silent */
    }
  }

  /**
   * @param {MouseEvent} evt - Click event.
   */
  function onClick(evt) {
    try {
      // Programmatic clicks (el.click() from other scripts) are not user actions.
      if (!evt.isTrusted) return;
      var node = evt.target;
      // Firefox can report a Text node as the target; Text has no closest().
      var target = node && node.nodeType === 1 ? node : node && node.parentElement;
      var el = target && target.closest ? target.closest('[data-track-click]') : null;
      if (!el) return;
      send(payloadFor(el));
    } catch (e) {
      /* silent: tracking must never break a click */
    }
  }

  document.addEventListener('click', onClick, { capture: true, passive: true });

  window.usAnalyticsTracking = { send: send, payloadFor: payloadFor };
})();
